import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Observable } from 'rxjs';

import { TeamDetailStateService } from '../../features/statistics/controller/team-detail-state.service';

import { TeamDetailHeaderComponent } from '../../features/statistics/view/team-detail-header/team-detail-header.component';
import { TeamSeasonSummaryComponent } from '../../features/statistics/view/team-season-summary/team-season-summary.component';
import { TeamRosterStatsComponent } from '../../features/statistics/view/team-roster-stats/team-roster-stats.component';
import { TeamMatchListComponent } from '../../features/statistics/view/team-match-list/team-match-list.component';

import {
  PaginatedSearchResultMatchSearchResultDTO,
  PlayerRosterStatsDTO,
  Season,
  TeamInfoDTO,
  TeamSeasonStatsDTO
} from '@solverml/api';

/**
 * Página "Smart" para la vista de detalle de un equipo.
 * Orquesta todos los componentes "Dumb" y se comunica
 * con TeamDetailStateService.
 */
@Component({
  selector: 'app-team-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    RouterLink,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    TeamDetailHeaderComponent,
    TeamSeasonSummaryComponent,
    TeamRosterStatsComponent,
    TeamMatchListComponent
  ],
  templateUrl: './team-detail-page.component.html',
  styleUrls: ['./team-detail-page.component.scss'],
  /**
   * Proveemos el StateService A NIVEL DE COMPONENTE.
   * El estado se crea al navegar aquí y se destruye al salir.
   */
  providers: [TeamDetailStateService]
})
export class TeamDetailPageComponent implements OnInit, OnDestroy {

  private state = inject(TeamDetailStateService);
  private route = inject(ActivatedRoute);

  // --- Observables para el Template ---

  // Carga principal (Info + Temporadas)
  public isLoading$: Observable<boolean>;
  public teamInfo$: Observable<TeamInfoDTO | null>;
  public seasons$: Observable<Season[] | null>;

  // Carga de datos de temporada (Stats, Roster, Partidos)
  public isLoadingSeasonData$: Observable<boolean>;
  public selectedSeasonStats$: Observable<TeamSeasonStatsDTO | null>;
  public roster$: Observable<PlayerRosterStatsDTO[] | null>;
  public matchesResult$: Observable<PaginatedSearchResultMatchSearchResultDTO | null>;

  // Temporada seleccionada (para el <mat-select>)
  public selectedSeasonId$: Observable<number | null>;

  constructor() {
    // Carga principal
    this.isLoading$ = this.state.isLoading$;
    this.teamInfo$ = this.state.teamInfo$;
    this.seasons$ = this.state.seasons$;

    // Carga de temporada
    this.isLoadingSeasonData$ = this.state.isLoadingSeasonData$;
    this.selectedSeasonStats$ = this.state.selectedSeasonStats$;
    this.roster$ = this.state.roster$;
    this.matchesResult$ = this.state.matchesResult$;

    // Valor del selector
    this.selectedSeasonId$ = this.state.selectedSeasonId$;
  }

  ngOnInit(): void {
    // Obtener el ID del equipo de la ruta
    const teamId = this.route.snapshot.paramMap.get('id');

    // Si tenemos ID, cargar los datos iniciales
    if (teamId) {
      this.state.loadInitialData(teamId);
    } else {
      console.error("No team ID found in route");
    }
  }

  ngOnDestroy(): void {
    // Resetear el estado al salir de la página
    this.state.resetState();
  }

  // --- Manejadores de Eventos ---

  /**
   * Se dispara desde el (selectionChange) del <mat-select> de temporadas.
   */
  public onSeasonChange(event: MatSelectChange): void {
    const seasonId = event.value as number;
    if (seasonId) {
      this.state.changeSeason(seasonId);
    }
  }

  /**
   * Se dispara desde el (pageChanged) de <app-team-match-list>.
   */
  public onPaginateMatches(page: number): void {
    this.state.paginateMatches(page);
  }
}
